import {JobNodes as Nodes, makeJobTaskDescriptor} from "@cm/job-nodes/job-nodes"
import type {JsonObjectUtils} from "@cm/job-nodes/json-object-utils"
import type {Config, ConvertedMap, ConversionConfig, Folder, RecordKeysForValueType, UpdateOp} from "#material-maps-exporter/targets/pbr/types"
import {UPDATE_OP} from "#material-maps-exporter/targets/pbr/types"

type EntityId = number

// Traversal helpers

export function descendSeqForStructOrArray<RootT extends object, StructT extends object>(
    root: RootT,
    target: StructT,
): {found: boolean; seq: UpdateOp["descendSeq"]} {
    let seq: UpdateOp["descendSeq"] = []
    let found: boolean

    const traverse = (x: object): boolean => {
        if (!(x instanceof Object)) return false

        if (x instanceof Array) {
            for (let idx = 0; idx < x.length; idx++) {
                if (x[idx] === target || traverse(x[idx])) {
                    seq = [{type: "ArrayElementDescend", idx: idx}, ...seq]
                    return true
                }
            }
        } else if (x instanceof Object) {
            for (const key of Object.keys(x)) {
                const val: any = (x as any)[key]
                if (key in x && (val === target || traverse(val))) {
                    seq = [{type: "StructFieldDescend", key: key}, ...seq]
                    return true
                }
            }
        }
        return false
    }

    if ((root as any) === (target as any)) {
        found = true
    } else {
        found = traverse(root)
    }

    return {found, seq}
}

type UpdateStructT = Config | Folder | ConversionConfig | ConvertedMap | JsonObjectUtils.DataObjectReference
export function createConfigStructFieldUpdateOp<T extends UpdateStructT, K extends keyof T extends string ? keyof T : never>(
    config: Config,
    struct: T,
    key: K,
    value: T[K],
): UpdateOp {
    const {found, seq} = descendSeqForStructOrArray(config, struct)
    if (!found) throw Error("Failed to build the descend sequence")

    return {
        type: "UpdateOp",
        descendSeq: seq,
        update: {
            type: "StructFieldUpdate",
            key: key,
            value: value,
        },
    }
}

function applyUpdateOp<RootT extends Record<string, any>>(root: RootT, updateOp: UpdateOp): RootT {
    let obj: any = root

    updateOp.descendSeq.map((descend) => {
        if (descend.type === "ArrayElementDescend") {
            if (!(obj instanceof Array)) throw Error("Expected array instance!")
            obj = obj[descend.idx]
        } else if (descend.type === "StructFieldDescend") {
            if (!(obj instanceof Object)) throw Error("Expected an object!")
            obj = obj[descend.key]
        } else {
            throw Error(`Unknown descend type: ${(descend as any).type}`)
        }
    })

    const update = updateOp.update

    if (update.type === "ArrayElementUpdate") {
        if (update.option === "append") {
            obj.push(update.value)
        } else if (update.option === "overwrite") {
            obj[update.idx] = update.value
        } else {
            throw Error(`Unknown option for array element update: ${(update as any).option}`)
        }
    } else if (updateOp.update.type === "StructFieldUpdate") {
        obj[update.key] = update.value
    } else {
        throw Error(`Unknown update type: ${(updateOp.update as any).type}`)
    }

    return root
}

export function applyConfigUpdateOp(config: Config, updateOp: UpdateOp): Config {
    return applyUpdateOp<Config>(config, updateOp)
}

// Job task for updating an export config stored as a JsonObject

export function createConfigFieldUpdateOpForDataObjectReference<T extends Record<string, any>, U extends Nodes.TypedDataNode<any>>(
    config: Config,
    struct: T,
    key: RecordKeysForValueType<T, JsonObjectUtils.DataObjectReference>,
    dataObjectRef: U,
    dataObjectAssignmentType: number,
) {
    const {found, seq} = descendSeqForStructOrArray(config, struct)
    if (!found) throw Error("Failed to build the descend sequence")

    const updateOp: Nodes.TypedDataNode<UpdateOp> = Nodes.struct({
        type: Nodes.value(UPDATE_OP),
        descendSeq: Nodes.value(seq),
        update: Nodes.struct({
            type: Nodes.value("StructFieldUpdate" as const),
            key: Nodes.value(key),
            value: Nodes.struct({
                type: Nodes.value("jsonObjectDataObjectReference"),
                dataObjectId: Nodes.struct({
                    type: Nodes.value("unwrapJobSystemDataObjectReference"),
                    ref: dataObjectRef,
                }),
                dataObjectAssignmentType: Nodes.value(dataObjectAssignmentType),
            }),
        }),
    })
    return updateOp
}

export type MaterialMapsExporterUpdateExportConfigInput = {
    jsonObjectId: EntityId
    updateOps: UpdateOp[]
}

export const materialMapsExporterUpdateExportConfigTask = makeJobTaskDescriptor<MaterialMapsExporterUpdateExportConfigInput, {}>(
    "MaterialMapsExporter.updateExportConfig",
    1,
)
